import { firstVisible, whenElementVisible, escapeHtml, accentedHtml, dropStaleHighlights } from './helpers';

// Copy and order come from Tours::SuggestionsIntro; this side owns what each step points at, under the same `key`.

const CARD_SELECTOR = '.suggestion-card-container';
const ACTIVE_SLIDE = '.carousel-item.active';

// `scoped` resolves within one card — list view stacks a dozen, and consecutive steps must share one.
// The `:has` clauses skip cards that render the row empty or the picture without a modal: still a layout
// box for driver.js to spotlight, but nothing the step is about.
const TARGETS = {
  browse: { selector: '#suggestionsNextButtonContainer .navigation-arrow-button', side: 'left', align: 'center' },
  actions: { selector: '.action-button-section:has(.action-button-container)', scoped: true, side: 'bottom', align: 'center' },
  'full-page': { selector: '.full-page-link', scoped: true, side: 'right', align: 'start' },
  image: { selector: '.suggestion-image-circle:has(.info-modal-button)', scoped: true, side: 'right', align: 'start' },
  type: { selector: '[data-tour-filter="suggestion_type"]', side: 'left', align: 'start' },
  sort: { selector: '[data-tour-filter="sort_order"]', side: 'left', align: 'start' },
  view: { selector: '[data-tour-filter="view"]', side: 'left', align: 'start' },
  more: { selector: '[data-tour-filter="more"] .right-sidebar-collapsible-content', side: 'left', align: 'end' },
};

// Active slide first: every card is briefly displayed while its charts render, so the first visible one
// is not necessarily the one on screen.
const currentCard = () => firstVisible(`${ACTIVE_SLIDE} ${CARD_SELECTOR}`) ?? firstVisible(CARD_SELECTOR);

const resolve = (key) => {
  const target = TARGETS[key];
  if (!target) return null;
  if (!target.scoped) return firstVisible(target.selector);

  const card = currentCard();
  return card ? firstVisible(target.selector, card) : null;
};

export default {
  mount({ start, payload }) {
    const steps = (payload.steps || []).filter(({ key }) => TARGETS[key]);
    if (!steps.length) return undefined;

    const stepFor = (step) => ({
      // A function, not a node: a scoped step belongs to whichever slide is active when it renders.
      element: () => resolve(step.key),
      popover: {
        title: escapeHtml(step.title),
        description: accentedHtml(step.description),
        side: TARGETS[step.key].side,
        align: TARGETS[step.key].align,
      },
      onHighlightStarted: (element) => dropStaleHighlights(element),
    });

    // driver.js centres a step whose element resolves null — e.g. carousel-arrow copy in list view.
    const begin = () => {
      const present = steps.filter(({ key }) => resolve(key));
      if (!present.length) return;

      start({ steps: present.map(stepFor) });
    };

    // The cards sit behind the content hider until the extension is proven present.
    return whenElementVisible(CARD_SELECTOR, begin);
  },
};
